/**
 * NavSparksIndicator - Compact sparks balance indicator for the navbar.
 * Desktop: HoverCard dropdown | Mobile: Drawer bottom-sheet
 * Uses existing useSparksWallet() hook (no new queries).
 */

import React, { useState, lazy, Suspense, forwardRef } from 'react';
import { Flame, ShoppingCart, GraduationCap, CalendarClock } from 'lucide-react';
import { usePVRedirect } from '@/hooks/usePVRedirect';
import { useRole } from '@/contexts/RoleContext';
import { useSparksWallet } from '@/hooks/useSparksWallet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSparksSettings } from '@/hooks/useSparksSettings';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';

const SparksPurchaseModal = lazy(() => import('@/components/sparks/SparksPurchaseModal'));

interface IndicatorButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  balance: number;
}

// forwardRef so Radix triggers (HoverCardTrigger / DrawerTrigger asChild)
// can pass refs without React warnings. Defined at module scope to avoid
// re-creating the component on every render of the parent.
const IndicatorButton = forwardRef<HTMLButtonElement, IndicatorButtonProps>(
  ({ balance, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      {...props}
      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border/50 bg-secondary/50 text-xs font-medium text-foreground transition-all duration-150 hover:border-primary/40 hover:shadow-[0_0_8px_hsl(var(--primary)/0.15)] cursor-pointer"
    >
      <Flame className="w-3.5 h-3.5 text-primary" />
      <span>{balance.toLocaleString('pt-BR')}</span>
    </button>
  ),
);
IndicatorButton.displayName = 'NavSparksIndicatorButton';

function SparksDropdownContent({ 
  balance, 
  dailyAmount,
  isVisitor, 
  canBuy,
  onBuy 
}: { 
  balance: number; 
  dailyAmount: number | null;
  isVisitor: boolean; 
  canBuy: boolean;
  onBuy: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Flame className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">
          Você tem {balance.toLocaleString('pt-BR')} Faíscas
        </span>
      </div>

      {dailyAmount != null && dailyAmount > 0 && (
        <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground leading-relaxed">
          <CalendarClock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Você recebe {dailyAmount} Faíscas por dia (expiram à meia-noite)</span>
        </div>
      )}

      {isVisitor ? (
        <>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Seu saldo diário é renovado automaticamente.
          </p>
          <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={onBuy}>
            <GraduationCap className="w-3.5 h-3.5" />
            Vire aluno para desbloquear mais
          </Button>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Use suas Faíscas para executar ferramentas premium.
          </p>
          {canBuy && (
            <Button 
              size="sm" 
              className="w-full gap-1.5 text-xs"
              onClick={onBuy}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              Comprar mais Faíscas
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default function NavSparksIndicator() {
  const { effectiveRole } = useRole();
  const { data: wallet } = useSparksWallet();
  const isMobile = useIsMobile();
  const [showPurchase, setShowPurchase] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { redirectToPV } = usePVRedirect();

  const { data: settings } = useSparksSettings();

  const isVisitor = effectiveRole === 'visitor';
  const canBuy = !isVisitor;
  const balance = wallet?.balance_int ?? 0;
  const dailyAmount = settings?.visitor_daily_amount_int ?? null;

  const handleBuy = () => {
    setDrawerOpen(false);
    if (isVisitor) {
      redirectToPV('nav-sparks');
    } else {
      setShowPurchase(true);
    }
  };

  if (isMobile) {
    return (
      <>
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <IndicatorButton balance={balance} />
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle className="flex items-center gap-2 justify-center">
                <Flame className="w-5 h-5 text-primary" />
                Faíscas
              </DrawerTitle>
              <DrawerDescription className="sr-only">Seu saldo de Faíscas</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-6">
              <SparksDropdownContent
                balance={balance}
                dailyAmount={dailyAmount}
                isVisitor={isVisitor}
                canBuy={canBuy}
                onBuy={handleBuy}
              />
            </div>
          </DrawerContent>
        </Drawer>

        {showPurchase && (
          <Suspense fallback={null}>
            <SparksPurchaseModal
              open={showPurchase}
              onOpenChange={setShowPurchase}
            />
          </Suspense>
        )}
      </>
    );
  }

  return (
    <>
      <HoverCard openDelay={100} closeDelay={200}>
        <HoverCardTrigger asChild>
          <IndicatorButton balance={balance} />
        </HoverCardTrigger>
        <HoverCardContent align="start" sideOffset={8} className="w-64 p-4">
          <SparksDropdownContent
            balance={balance}
            dailyAmount={dailyAmount}
            isVisitor={isVisitor}
            canBuy={canBuy}
            onBuy={handleBuy}
          />
        </HoverCardContent>
      </HoverCard>

      {showPurchase && (
        <Suspense fallback={null}>
          <SparksPurchaseModal
            open={showPurchase}
            onOpenChange={setShowPurchase}
          />
        </Suspense>
      )}
    </>
  );
}
